import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api, { users, workoutExecution, workoutPlans } from '../services/api';
import { applyPlanDiff, createPlanBody, formatPrescription } from '../services/planDiff';
import PlanGeneratorDialog from '../components/WorkoutPlanStudio/PlanGeneratorDialog';
import PlanCanvas from '../components/PlanStudio/PlanCanvas';
import RecoveryHeatmap from '../components/PlanStudio/RecoveryHeatmap';
import PlanPreview from '../components/PlanStudio/PlanPreview';
import DemoOverlay from '../components/WorkoutPlayer/DemoOverlay';
import { resolveDemo } from '../components/WorkoutPlayer/demoRegistry';
import '../styles/workout-plan-studio.css';

/** Verdadeiro enquanto a janela cumpre a media query (o estúdio muda de três colunas para uma só em ecrãs estreitos). */
export const useMediaQuery = (query) => {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener('change', onChange); else mql.addListener(onChange);
    return () => { if (mql.removeEventListener) mql.removeEventListener('change', onChange); else mql.removeListener(onChange); };
  }, [query]);
  return matches;
};

const daysPt = [
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
  'Domingo',
];

const clampInt = (v, min, max, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
};

/**
 * A semana leve do atleta, do lado de quem o acompanha: o treinador marca (ou desfaz) a semana e vê a redução que
 * isso aplica. A sugestão mostra há quantas semanas o atleta treina sem parar; nada muda sem este toque.
 */
const DeloadControl = ({ userId }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    workoutExecution.deload(null, userId).then((r) => { if (alive) setStatus(r.data || null); }).catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [userId]);
  const act = async (on) => {
    setBusy(true);
    try {
      const r = on ? await workoutExecution.markDeload(null, userId) : await workoutExecution.unmarkDeload(null, userId);
      setStatus(r.data || null);
      toast.success(on ? 'Semana marcada como leve.' : 'Semana leve desfeita.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Não foi possível alterar a semana leve.');
    } finally {
      setBusy(false);
    }
  };
  if (!status) return null;
  return (
    <div data-testid="studio-deload" style={{ marginTop: 12, padding: 12, border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--info)' }}>Semana leve deste atleta</div>
      {status.isLight ? (
        <>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 8px' }}>
            Esta semana está marcada como leve: {status.reduction}. Já está aplicada ao treino que ele vê.
          </p>
          <button type="button" onClick={() => act(false)} disabled={busy} style={secondaryBtn} data-testid="studio-deload-undo">
            Desfazer (volta ao normal)
          </button>
        </>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 8px' }}>
            {status.suggestion ? status.suggestion.text : 'Esta semana não está marcada como leve.'}
          </p>
          <button type="button" onClick={() => act(true)} disabled={busy} style={secondaryBtn} data-testid="studio-deload-mark">
            Marcar esta semana como leve
          </button>
        </>
      )}
    </div>
  );
};

const AdminWorkoutPlanStudioPage = () => {
  const narrow = useMediaQuery('(max-width: 899px)');
  const editorRef = useRef(null);
  const inspectorRef = useRef(null);
  // Num ecrã estreito as três colunas empilham-se; levar o editor/inspetor à vista depois de uma escolha evita "nada aconteceu".
  const bringIntoView = (ref) => {
    if (!narrow) return;
    setTimeout(() => { try { ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* sem scroll suave */ } }, 60);
  };
  // Para onde vai o exercício quando se toca em "➕" na biblioteca (arrastar não existe em ecrãs tácteis).
  const [addTargetDay, setAddTargetDay] = useState(0);
  const [tab, setTab] = useState('plans'); // plans | library

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [plansSearch, setPlansSearch] = useState('');

  const [userList, setUserList] = useState([]);

  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [libFilters, setLibFilters] = useState({ muscleGroup: '', equipment: '', difficulty: '' });

  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [editorMode, setEditorMode] = useState('view'); // view | edit | create
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState(''); // '' | Template | MemberPlan

  // Atribuir um modelo a um sócio / copiar um plano: { mode: 'assign' | 'copy-member' | 'copy-template', userId, title }
  const [copyDialog, setCopyDialog] = useState(null);
  // Substituir um exercício do plano pelo da biblioteca: { exerciseId, search }
  const [replaceDialog, setReplaceDialog] = useState(null);
  // O gerador de planos (proposta → rascunho no editor).
  const [showGenerator, setShowGenerator] = useState(false);
  // Superset no estúdio: os exercícios do dia escolhidos para juntar ao exercício selecionado (máx. 2, total 3).
  const [supersetPick, setSupersetPick] = useState({ forId: null, ids: [] });

  const [selectedExercisePath, setSelectedExercisePath] = useState(null); // { dayIndex, exIndex }

  const [showNewLibModal, setShowNewLibModal] = useState(false);
  // A demonstração (vídeo/animacão) de um exercício da biblioteca, aberta a partir da lista do estúdio.
  const [libraryDemo, setLibraryDemo] = useState(null);
  const [libForm, setLibForm] = useState({
    name: '',
    description: '',
    instructions: '',
    muscleGroup: '',
    equipment: '',
    difficulty: 'Iniciante',
    videoUrl: '',
    imageUrl: '',
    gifUrl: '',
    caloriesPerMinute: 5,
    isActive: true,
  });
  const libFileInputRef = useRef(null);
  const libGifInputRef = useRef(null);
  const libVideoInputRef = useRef(null);


  /**
   * Ver como o exercício se faz, antes de o pôr no plano: a ficha vem da biblioteca e a demonstração usa o mesmo
   * visualizador do treino do atleta (vídeo/GIF/animacão, pela ordem aprovada). Sem ficha não se inventa nada.
   */
  const openLibraryDemo = async (item) => {
    setLibraryDemo({ name: item.name, demo: null, loading: true });
    try {
      const res = await api.get(`/exercise-library/${item.id}`);
      const d = res?.data || {};
      const name = d.name || item.name;
      const source = {
        name, muscles: d.muscles || [], equipment: d.equipmentLinks || [], media: d.media || [],
        steps: d.steps || [], mistakes: d.mistakes || [], tip: d.tip || null,
        imageUrl: d.imageUrl, videoUrl: d.videoUrl, gifUrl: d.gifUrl,
      };
      setLibraryDemo({ name, demo: resolveDemo({ name }, source), loading: false });
    } catch {
      setLibraryDemo(null);
      toast.error('Não foi possível abrir a demonstração deste exercício.');
    }
  };

  const emptyPlan = (planType = 'MemberPlan') => ({
    id: null,
    planType,
    userId: '',
    title: '',
    description: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    workoutDays: [
      {
        dayName: 'Segunda-feira',
        orderIndex: 0,
        notes: '',
        exercises: [],
      },
    ],
  });

  const [draft, setDraft] = useState(emptyPlan);

  const loadTaxonomy = async (force = false) => {
    try {
      if (taxonomyLoading) return;
      if (!force && muscleGroups.length && equipmentTypes.length) return;
      setTaxonomyLoading(true);
      const [mgRes, eqRes] = await Promise.all([
        api.get('/exercise-taxonomy/muscle-groups', { params: { activeOnly: true } }),
        api.get('/exercise-taxonomy/equipment-types', { params: { activeOnly: true } }),
      ]);
      const mg = Array.isArray(mgRes.data) ? mgRes.data : [];
      const eq = Array.isArray(eqRes.data) ? eqRes.data : [];
      setMuscleGroups(mg);
      setEquipmentTypes(eq);
    } catch {
      // Non-blocking; library still works with free-text values.
    } finally {
      setTaxonomyLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'library') loadTaxonomy(false);
  }, [tab]);

  useEffect(() => {
    if (showNewLibModal) loadTaxonomy(false);
  }, [showNewLibModal]);

  const muscleOptions = useMemo(() => {
    const fromTax = (muscleGroups || [])
      .filter(x => x && x.name)
      .slice()
      .sort((a, b) => (Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) || String(a.name).localeCompare(String(b.name)));
    if (fromTax.length) return fromTax.map(x => String(x.name));
    return Array.from(new Set((library || []).map(x => x.muscleGroup).filter(Boolean))).sort();
  }, [muscleGroups, library]);

  const equipmentOptions = useMemo(() => {
    const fromTax = (equipmentTypes || [])
      .filter(x => x && x.name)
      .slice()
      .sort((a, b) => (Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) || String(a.name).localeCompare(String(b.name)));
    if (fromTax.length) return fromTax.map(x => String(x.name));
    return Array.from(new Set((library || []).map(x => x.equipment).filter(Boolean))).sort();
  }, [equipmentTypes, library]);

  const selectedMuscleMeta = useMemo(() => {
    const name = String(libForm.muscleGroup || '').trim();
    if (!name) return null;
    return (muscleGroups || []).find(x => String(x?.name || '').trim() === name) || null;
  }, [libForm.muscleGroup, muscleGroups]);

  const selectedEquipmentMeta = useMemo(() => {
    const name = String(libForm.equipment || '').trim();
    if (!name) return null;
    return (equipmentTypes || []).find(x => String(x?.name || '').trim() === name) || null;
  }, [libForm.equipment, equipmentTypes]);

  const filteredPlans = useMemo(() => {
    const s = plansSearch.trim().toLowerCase();
    return plans.filter(p =>
      (!typeFilter || p.planType === typeFilter) &&
      (!s ||
        String(p.title || '').toLowerCase().includes(s) ||
        String(p.userName || '').toLowerCase().includes(s))
    );
  }, [plans, plansSearch, typeFilter]);

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId) return null;
    return plans.find(p => p.id === selectedPlanId) || null;
  }, [plans, selectedPlanId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // A lista de utilizadores pode não estar ao alcance de quem só gere planos: sem ela escolhe-se o sócio de outra forma, não se perde o ecrã.
      const [plansRes, usersRes] = await Promise.all([
        workoutPlans.getAll(),
        users.getAll().catch(() => ({ data: [] })),
      ]);
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      setUserList(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar dados');
      setPlans([]);
      setUserList([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLibrary = async () => {
    setLibLoading(true);
    try {
      const params = {
        search: libSearch || undefined,
        muscleGroup: libFilters.muscleGroup || undefined,
        equipment: libFilters.equipment || undefined,
        difficulty: libFilters.difficulty || undefined,
        activeOnly: false,
        take: 300,
      };
      const res = await api.get('/exercise-library', { params });
      setLibrary(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar biblioteca de exercícios');
      setLibrary([]);
    } finally {
      setLibLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'library') loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const startCreate = (planType = 'MemberPlan') => {
    setEditorMode('create');
    setSelectedPlanId(null);
    setSelectedExercisePath(null);
    setAddTargetDay(0);
    setDraft(emptyPlan(planType));
    bringIntoView(editorRef);
  };

  // Uma proposta do gerador abre o editor em "criar", já preenchido: nada está gravado até se carregar em Guardar.
  const startFromGenerated = (generated) => {
    setShowGenerator(false);
    setEditorMode('create');
    setSelectedPlanId(null);
    setSelectedExercisePath(null);
    setAddTargetDay(0);
    setDraft({ ...emptyPlan(generated.planType), ...generated });
    bringIntoView(editorRef);
    toast.info('Proposta pronta: reveja, escolha o atleta (ou guarde como modelo) e grave.');
  };

  // O rascunho leva os Ids do servidor (do plano, dos dias e dos exercícios): é por eles que a gravação
  // sabe o que é novo, o que mudou e o que foi retirado, sem recriar nada.
  const toDraft = (plan) => ({
    id: plan.id,
    planType: plan.planType || 'MemberPlan',
    userId: String(plan.userId || ''),
    title: plan.title || '',
    description: plan.description || '',
    notes: plan.notes || '',
    startDate: plan.startDate ? String(plan.startDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
    endDate: plan.endDate ? String(plan.endDate).slice(0, 10) : '',
    workoutDays: (plan.workoutDays || []).map((d, i) => ({
      id: d.id,
      dayName: d.dayName || daysPt[i % daysPt.length],
      orderIndex: typeof d.orderIndex === 'number' ? d.orderIndex : i,
      notes: d.notes || '',
      exercises: (d.exercises || []).map((ex, exIdx) => ({
        id: ex.id,
        name: ex.name || '',
        setsTarget: ex.setsTarget ?? '',
        repsMin: ex.repsMin ?? '',
        repsMax: ex.repsMax ?? '',
        restSeconds: ex.restSeconds ?? '',
        targetWeightKg: ex.targetWeightKg ?? '',
        tempo: ex.tempo || '',
        trackingType: ex.trackingType || 'weight-reps',
        orderIndex: typeof ex.orderIndex === 'number' ? ex.orderIndex : exIdx,
        notes: ex.notes || '',
        videoUrl: ex.videoUrl || '',
        exerciseLibraryId: ex.exerciseLibraryId ?? null,
        imageUrl: ex.imageUrl || '',
        gifUrl: ex.gifUrl || '',
        isLegacy: !!ex.isLegacy,
        originalName: ex.originalName || '',
        replacedAt: ex.replacedAt || null,
        supersetGroup: ex.supersetGroup ?? null,
      })),
    })),
  });

  const startEdit = () => {
    if (!selectedPlan || !selectedPlan.canManage) return;
    setEditorMode('edit');
    setSelectedExercisePath(null);
    setAddTargetDay(0);
    setDraft(toDraft(selectedPlan));
    bringIntoView(editorRef);
  };

  const cancelEdit = () => {
    setEditorMode('view');
    setSelectedExercisePath(null);
    if (selectedPlanId && selectedPlan) {
      // re-select just to keep UI consistent
      setSelectedPlanId(selectedPlanId);
    }
  };

  const savePlan = async () => {
    if (!draft.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    const isTemplate = draft.planType === 'Template';
    if (!isTemplate && !draft.userId) {
      toast.error('Selecione um utilizador');
      return;
    }

    setSaving(true);
    try {
      if (editorMode === 'edit' && draft.id) {
        // Edita-se por diferença: o servidor mantém os Ids de tudo o que não foi retirado, e é por eles que o histórico liga ao plano.
        const original = plans.find(p => p.id === draft.id);
        if (!original) throw new Error('O plano já não está na lista; atualize a página.');
        const { changes, warnings } = await applyPlanDiff(workoutPlans, draft.id, original, draft);
        warnings.forEach(w => toast.warn(w));
        toast.success(changes === 0 ? 'Sem alterações' : isTemplate ? 'Modelo atualizado ✅' : 'Plano atualizado ✅');
      } else {
        const created = await workoutPlans.create(createPlanBody(draft, { planType: draft.planType, userId: draft.userId }));
        setSelectedPlanId(created?.data?.id ?? null);
        toast.success(isTemplate ? 'Modelo criado ✅' : 'Plano criado ✅');
      }

      await loadAll();
      setEditorMode('view');
    } catch (e) {
      console.error(e);
      toast.error(errorMessage(e, 'Erro ao guardar plano'));
      // Pode ter ficado gravada parte da edição (cada operação grava logo): mostra-se o que o servidor tem.
      if (editorMode === 'edit') await loadAll();
    } finally {
      setSaving(false);
    }
  };

  const errorMessage = (e, fallback) => {
    const data = e?.response?.data;
    if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(' · ');
    return data?.message || (typeof data === 'string' && data) || e?.message || fallback;
  };

  // ── Modelos: atribuir a um sócio (cópia independente), copiar, guardar como modelo ──

  const openCopyDialog = (mode) => {
    if (!selectedPlan) return;
    setCopyDialog({ mode, userId: '', title: mode === 'copy-template' ? `${selectedPlan.title} (${selectedPlan.planType === 'Template' ? 'cópia' : 'modelo'})` : selectedPlan.title });
  };

  const submitCopyDialog = async () => {
    const d = copyDialog;
    if (!d || !selectedPlan) return;
    if (d.mode !== 'copy-template' && !d.userId) {
      toast.error('Selecione o atleta');
      return;
    }
    setSaving(true);
    try {
      let res;
      if (d.mode === 'assign') {
        res = await workoutPlans.assign(selectedPlan.id, { userId: Number(d.userId), title: d.title || undefined });
        toast.success('Modelo atribuído: o atleta tem a sua própria cópia ✅');
      } else if (d.mode === 'copy-member') {
        res = await workoutPlans.copy(selectedPlan.id, { targetType: 'MemberPlan', userId: Number(d.userId), title: d.title || undefined });
        toast.success('Plano copiado para o atleta ✅');
      } else {
        res = await workoutPlans.copy(selectedPlan.id, { targetType: 'Template', title: d.title || undefined });
        toast.success('Guardado como modelo ✅');
      }
      setCopyDialog(null);
      await loadAll();
      setSelectedPlanId(res?.data?.id ?? selectedPlan.id);
      setEditorMode('view');
    } catch (e) {
      console.error(e);
      toast.error(errorMessage(e, 'Não foi possível copiar o plano'));
    } finally {
      setSaving(false);
    }
  };

  const openReplace = async (ex) => {
    if (!ex?.id) return;
    setReplaceDialog({ exerciseId: ex.id, name: ex.name, search: '', alternatives: [], loading: !!ex.exerciseLibraryId });
    if (!ex.exerciseLibraryId) return;
    try {
      // As sugestões vêm do servidor: primeiro as curadas (as mesmas que o player usa) e, se faltarem, equivalentes
      // propostos pelo motor — mesmo grupo muscular, mesmo padrão de movimento e equipamento que o ginásio tem,
      // sempre com o motivo escrito. Nenhuma é aplicada sozinha.
      const res = await api.get(`/exercise-library/${ex.exerciseLibraryId}/substitutes/suggested`, { params: { take: 6 } });
      const subs = Array.isArray(res.data?.items) ? res.data.items.filter(s => s.isActive !== false) : [];
      setReplaceDialog(prev => (prev && prev.exerciseId === ex.id ? { ...prev, alternatives: subs, curatedCount: res.data?.curatedCount ?? 0, loading: false } : prev));
    } catch (e) {
      console.error(e);
      setReplaceDialog(prev => (prev && prev.exerciseId === ex.id ? { ...prev, loading: false } : prev));
    }
  };

  // ── Substituir um exercício já gravado pelo da biblioteca ──
  // Aplica-se logo (é uma operação do servidor sobre a mesma linha: o Id, a posição e a prescrição ficam);
  // o resto do rascunho não se perde: só se atualiza o exercício substituído.
  const replaceExercise = async (libItem) => {
    if (!replaceDialog || !draft.id) return;
    setSaving(true);
    try {
      const res = await workoutPlans.replaceExercise(draft.id, replaceDialog.exerciseId, libItem.id);
      const replaced = res?.data?.exercise;
      if (replaced) {
        setDraft(prev => ({
          ...prev,
          workoutDays: prev.workoutDays.map(day => ({
            ...day,
            exercises: (day.exercises || []).map(ex => ex.id === replaced.id
              ? {
                ...ex,
                name: replaced.name || ex.name,
                exerciseLibraryId: replaced.exerciseLibraryId ?? null,
                imageUrl: replaced.imageUrl || '',
                gifUrl: replaced.gifUrl || '',
                videoUrl: replaced.videoUrl || '',
                isLegacy: false,
                originalName: replaced.originalName || '',
                replacedAt: replaced.replacedAt || null,
                trackingType: replaced.trackingType || ex.trackingType,
                // Se o tipo de registo é outro, o servidor trocou a prescrição pela do novo exercício.
                ...(res.data.prescriptionKept ? {} : {
                  setsTarget: replaced.setsTarget ?? '', repsMin: replaced.repsMin ?? '', repsMax: replaced.repsMax ?? '',
                  restSeconds: replaced.restSeconds ?? '', targetWeightKg: replaced.targetWeightKg ?? '',
                }),
              }
              : ex),
          })),
        }));
      }
      // O servidor tem agora outra versão do exercício: atualiza a lista para o diff comparar com ela.
      const fresh = await workoutPlans.getAll();
      setPlans(Array.isArray(fresh.data) ? fresh.data : []);
      toast.success(res?.data?.prescriptionKept === false ? 'Exercício substituído — a prescrição passou a ser a do novo exercício' : 'Exercício substituído ✅');
      setReplaceDialog(null);
    } catch (e) {
      console.error(e);
      toast.error(errorMessage(e, 'Não foi possível substituir o exercício'));
    } finally {
      setSaving(false);
    }
  };

  const addDay = () => {
    setDraft(prev => {
      const nextIndex = prev.workoutDays.length;
      const nextDayName = daysPt[nextIndex % daysPt.length];
      return {
        ...prev,
        workoutDays: [...prev.workoutDays, { dayName: nextDayName, orderIndex: nextIndex, notes: '', exercises: [] }]
      };
    });
  };

  const removeDay = (dayIndex) => {
    setDraft(prev => ({
      ...prev,
      workoutDays: prev.workoutDays.filter((_, i) => i !== dayIndex)
    }));
    setSelectedExercisePath(null);
  };

  const addExerciseManual = (dayIndex) => {
    setDraft(prev => {
      const next = { ...prev };
      const day = next.workoutDays[dayIndex];
      day.exercises = [...(day.exercises || []), {
        name: '',
        setsTarget: 3,
        repsMin: 10,
        repsMax: 10,
        targetWeightKg: '',
        restSeconds: 60,
        tempo: '',
        trackingType: 'weight-reps',
        orderIndex: (day.exercises || []).length,
        notes: '',
        videoUrl: '',
        exerciseLibraryId: null,
        imageUrl: '',
        gifUrl: '',
      }];
      return next;
    });
    setSelectedExercisePath({ dayIndex, exIndex: (draft.workoutDays[dayIndex]?.exercises?.length || 0) });
  };

  const removeExercise = (dayIndex, exIndex) => {
    setDraft(prev => {
      const next = { ...prev };
      const day = next.workoutDays[dayIndex];
      day.exercises = (day.exercises || []).filter((_, i) => i !== exIndex);
      return next;
    });
    setSelectedExercisePath(null);
  };

  const updateExerciseField = (dayIndex, exIndex, field, value) => {
    setDraft(prev => {
      const next = { ...prev };
      next.workoutDays = [...next.workoutDays];
      const day = { ...next.workoutDays[dayIndex] };
      day.exercises = [...(day.exercises || [])];
      day.exercises[exIndex] = { ...day.exercises[exIndex], [field]: value };
      next.workoutDays[dayIndex] = day;
      return next;
    });
  };

  const onDragStartLibrary = (e, libItem) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'library', item: libItem }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Acrescenta um exercício da biblioteca a um dia do rascunho. É o que o arrastar faz e o que o botão "➕" faz (ecrãs tácteis).
  const appendLibraryExercise = (dayIndex, libItem) => {
    setDraft(prev => {
      if (!prev.workoutDays?.[dayIndex]) return prev;
      const next = { ...prev };
      next.workoutDays = [...next.workoutDays];
      const day = { ...next.workoutDays[dayIndex] };
      day.exercises = [...(day.exercises || [])];

      day.exercises.push({
        name: libItem.name || '',
        setsTarget: libItem.defaultSets ?? 3,
        repsMin: libItem.defaultRepsMin ?? 10,
        repsMax: libItem.defaultRepsMax ?? libItem.defaultRepsMin ?? 10,
        targetWeightKg: '',
        restSeconds: libItem.defaultRestSeconds ?? 60,
        tempo: '',
        trackingType: libItem.trackingType || 'weight-reps',
        orderIndex: day.exercises.length,
        notes: libItem.instructions || '',
        videoUrl: libItem.videoUrl || '',
        exerciseLibraryId: libItem.id || null,
        imageUrl: libItem.imageUrl || '',
        gifUrl: libItem.gifUrl || '',
      });

      next.workoutDays[dayIndex] = day;
      return next;
    });
    toast.success('Exercício adicionado');
  };

  const onDropOnDay = (e, dayIndex) => {
    e.preventDefault();
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.type === 'library') { appendLibraryExercise(dayIndex, parsed.item); return; }
      // Mover um exercício de um dia para outro: tira-se de onde estava e põe-se no fim do dia de destino.
      if (parsed?.type === 'exercise') {
        setDraft(prev => {
          const from = prev.workoutDays?.[parsed.dayIndex];
          const to = prev.workoutDays?.[dayIndex];
          if (!from || !to || parsed.dayIndex === dayIndex) return prev;
          const moving = (from.exercises || [])[parsed.exIndex];
          if (!moving) return prev;
          const next = { ...prev };
          next.workoutDays = [...next.workoutDays];
          next.workoutDays[parsed.dayIndex] = { ...from, exercises: (from.exercises || []).filter((_, i) => i !== parsed.exIndex) };
          next.workoutDays[dayIndex] = { ...to, exercises: [...(to.exercises || []), { ...moving, orderIndex: (to.exercises || []).length }] };
          return next;
        });
        return;
      }
    } catch (err) {
      console.error(err);
    }
  };

  /** Arrastar um exercício do plano (para o mover para outro dia). */
  const onDragStartExercise = (e, dayIndex, exIndex) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'exercise', dayIndex, exIndex }));
    e.dataTransfer.effectAllowed = 'move';
  };

  /** O nome do dia escreve-se no próprio cartão. */
  const updateDayName = (dayIndex, value) => {
    setDraft(prev => {
      if (!prev.workoutDays?.[dayIndex]) return prev;
      const next = { ...prev };
      next.workoutDays = [...next.workoutDays];
      next.workoutDays[dayIndex] = { ...next.workoutDays[dayIndex], dayName: value };
      return next;
    });
  };

  const uploadToFolder = async (file, folder) => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/upload/image?folder=${encodeURIComponent(folder)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data?.url;
  };

  const saveLibraryItem = async () => {
    if (!libForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      const dto = {
        name: libForm.name,
        description: libForm.description || null,
        instructions: libForm.instructions || null,
        muscleGroup: libForm.muscleGroup || null,
        equipment: libForm.equipment || null,
        difficulty: libForm.difficulty || 'Iniciante',
        videoUrl: libForm.videoUrl || null,
        imageUrl: libForm.imageUrl || null,
        gifUrl: libForm.gifUrl || null,
        caloriesPerMinute: clampInt(libForm.caloriesPerMinute, 0, 999, 5),
        isActive: !!libForm.isActive,
      };
      await api.post('/exercise-library', dto);
      toast.success('Exercício criado ✅');
      setShowNewLibModal(false);
      setLibForm({
        name: '', description: '', instructions: '', muscleGroup: '', equipment: '', difficulty: 'Iniciante',
        videoUrl: '', imageUrl: '', gifUrl: '', caloriesPerMinute: 5, isActive: true,
      });
      loadLibrary();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Erro ao criar exercício');
    }
  };

  const inspector = useMemo(() => {
    if (!selectedExercisePath) return null;
    const d = draft.workoutDays?.[selectedExercisePath.dayIndex];
    const ex = d?.exercises?.[selectedExercisePath.exIndex];
    if (!ex) return null;
    return { day: d, ex };
  }, [draft, selectedExercisePath]);

  /**
   * Superset/circuito: agrupar este exercício com 1 ou 2 do mesmo dia. Grava-se logo (como a substituição), porque o
   * servidor é quem valida «2 a 3, do mesmo dia»; o rascunho e a lista passam a mostrar o grupo que ele devolveu.
   */
  const supersetMembersOf = (d, ex) => (ex?.supersetGroup ? (d.exercises || []).filter((x) => x.supersetGroup === ex.supersetGroup) : []);
  const applySupersetRows = (dayId, rows) => {
    const byId = new Map((rows || []).map((r) => [r.id, r.supersetGroup ?? null]));
    const withGroups = (days) => (days || []).map((d) => d.id !== dayId ? d : {
      ...d,
      exercises: (d.exercises || []).map((ex) => (ex.id && byId.has(ex.id) ? { ...ex, supersetGroup: byId.get(ex.id) } : ex)),
    });
    setDraft((prev) => ({ ...prev, workoutDays: withGroups(prev.workoutDays) }));
    setPlans((prev) => prev.map((p) => (p.id !== draft.id ? p : { ...p, workoutDays: withGroups(p.workoutDays) })));
  };
  const saveSuperset = async () => {
    const picked = supersetPick.forId === inspector?.ex?.id ? supersetPick.ids : [];
    const ids = [inspector?.ex?.id, ...picked].filter(Boolean);
    if (ids.length < 2) { toast.error('Escolha pelo menos mais um exercício do dia.'); return; }
    setSaving(true);
    try {
      const res = await workoutPlans.groupSuperset(draft.id, inspector.day.id, ids);
      applySupersetRows(inspector.day.id, res.data);
      setSupersetPick({ forId: null, ids: [] });
      toast.success('Superset criado: no treino aparece A1/A2 e o descanso é entre rondas.');
    } catch (e) {
      toast.error(errorMessage(e, 'Não foi possível criar o superset'));
    } finally {
      setSaving(false);
    }
  };
  const removeSuperset = async () => {
    setSaving(true);
    try {
      const res = await workoutPlans.ungroupSuperset(draft.id, inspector.day.id, inspector.ex.id);
      applySupersetRows(inspector.day.id, res.data);
      toast.success('Exercícios separados: cada um volta a ter o seu descanso.');
    } catch (e) {
      toast.error(errorMessage(e, 'Não foi possível desagrupar'));
    } finally {
      setSaving(false);
    }
  };

  // Antes de qualquer saída antecipada: o número de hooks tem de ser o mesmo em todos os renders.
  const isEditing = editorMode === 'edit' || editorMode === 'create';

  // De quem é o plano que está à frente: é este o atleta cuja recuperação se mostra e cujo telemóvel se pré-visualiza.
  const contextMemberId = useMemo(() => {
    const raw = isEditing ? draft?.userId : selectedPlan?.userId;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [draft?.userId, isEditing, selectedPlan?.userId]);
  const contextMemberName = isEditing ? (userList.find((u) => String(u.id) === String(draft?.userId))?.name || null) : (selectedPlan?.userName || null);

  if (loading) {
    return (
      <div style={{ padding: 24, color: 'var(--text-secondary)' }}>
        ⏳ A carregar…
      </div>
    );
  }

  return (
    <div className="wps-root" style={{ padding: narrow ? 12 : 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--info-text)' }}>🏋️ Planos de treino</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Criar e editar planos com a biblioteca de exercícios, imagens e arrastar e largar.</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => setTab('plans')} style={tabBtn(tab === 'plans')}>📋 Planos</button>
          <button onClick={() => setTab('library')} style={tabBtn(tab === 'library')}>🧩 Biblioteca</button>
        </div>
      </div>

      {tab === 'plans' && (
        <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '360px 1fr 360px', gap: 14, alignItems: 'start' }}>
          {/* Left: plans list */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 900 }}>Planos</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => startCreate('MemberPlan')} style={primaryBtn}>➕ Plano</button>
                <button onClick={() => startCreate('Template')} style={secondaryBtn}>➕ Modelo</button>
                <button onClick={() => setShowGenerator(true)} style={secondaryBtn} data-testid="open-generator">✨ Gerar</button>
              </div>
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...input, marginBottom: 8 }}>
              <option value="">Todos (planos e modelos)</option>
              <option value="MemberPlan">Planos de atletas</option>
              <option value="Template">Modelos</option>
            </select>
            <input value={plansSearch} onChange={(e) => setPlansSearch(e.target.value)} placeholder="Pesquisar por título ou utilizador…" style={input} />

            <div style={{ marginTop: 10, maxHeight: '70vh', overflow: 'auto' }}>
              {filteredPlans.map(p => (
                <div
                  key={p.id}
                  onClick={() => { setSelectedPlanId(p.id); setEditorMode('view'); setSelectedExercisePath(null); bringIntoView(editorRef); }}
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    border: '1px solid var(--border-color)',
                    background: p.id === selectedPlanId ? '#eff6ff' : '#fff',
                    cursor: 'pointer',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontWeight: 900, color: 'var(--info-text)' }}>
                    {p.title}
                    {p.planType === 'Template' && <span style={typeBadge}>Modelo</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {p.planType === 'Template' ? 'Modelo reutilizável' : (p.userName || '—')} • {p.workoutDays?.length || 0} dias
                  </div>
                </div>
              ))}
              {filteredPlans.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Sem planos.</div>
              )}
            </div>
          </div>

          {/* Center: editor */}
          <div style={card} ref={editorRef} data-testid="studio-editor">
            {!isEditing && !selectedPlan && (
              <div style={{ padding: 18, color: 'var(--text-secondary)' }}>Selecione um plano ou crie um novo.</div>
            )}

            {isEditing && (
              <>
                <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 220px', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={label}>Título</label>
                    <input value={draft.title} onChange={(e) => setDraft(prev => ({ ...prev, title: e.target.value }))} style={input} />
                  </div>
                  <div>
                    {draft.planType === 'Template' ? (
                      <>
                        <label style={label}>Tipo</label>
                        <div style={{ ...input, background: 'var(--info-bg)', color: 'var(--info)', fontWeight: 900 }}>Modelo reutilizável</div>
                      </>
                    ) : (
                      <>
                        <label style={label}>Utilizador</label>
                        {/* Um plano não muda de sócio: copia-se para o outro. */}
                        <select value={draft.userId} disabled={editorMode === 'edit'} onChange={(e) => setDraft(prev => ({ ...prev, userId: e.target.value }))} style={input}>
                          <option value="">—</option>
                          {userList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                          {draft.userId && !userList.some(u => String(u.id) === String(draft.userId)) && (
                            <option value={draft.userId}>{selectedPlan?.userName || `#${draft.userId}`}</option>
                          )}
                        </select>
                      </>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr 180px 180px', gap: 12, marginBottom: 12 }}>
                  <div style={narrow ? { gridColumn: '1 / -1' } : undefined}>
                    <label style={label}>Descrição</label>
                    <input value={draft.description} onChange={(e) => setDraft(prev => ({ ...prev, description: e.target.value }))} style={input} />
                  </div>
                  <div>
                    <label style={label}>Início</label>
                    <input type="date" value={draft.startDate} onChange={(e) => setDraft(prev => ({ ...prev, startDate: e.target.value }))} style={input} />
                  </div>
                  <div>
                    <label style={label}>Fim (opcional)</label>
                    <input type="date" value={draft.endDate} onChange={(e) => setDraft(prev => ({ ...prev, endDate: e.target.value }))} style={input} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  <button onClick={addDay} style={secondaryBtn}>➕ Adicionar dia</button>
                  <button onClick={loadLibrary} style={secondaryBtn}>🧩 Atualizar biblioteca</button>
                  <button onClick={savePlan} disabled={saving} style={primaryBtn}>{saving ? '⏳ A guardar…' : '💾 Guardar'}</button>
                  <button onClick={cancelEdit} disabled={saving} style={dangerBtn}>Cancelar</button>
                </div>

                <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 12 }}>
                  <PlanCanvas
                    days={draft.workoutDays || []}
                    narrow={narrow}
                    editing
                    selectedPath={selectedExercisePath}
                    onRenameDay={updateDayName}
                    onSelectExercise={(dayIndex, exIndex) => { setSelectedExercisePath({ dayIndex, exIndex }); bringIntoView(inspectorRef); }}
                    onFieldChange={updateExerciseField}
                    onAddExercise={addExerciseManual}
                    onRemoveExercise={removeExercise}
                    onRemoveDay={removeDay}
                    onDropDay={onDropOnDay}
                    onDragStartExercise={onDragStartExercise}
                    onReplace={openReplace}
                    onPreview={openLibraryDemo}
                  />
                </div>
              </>
            )}

            {!isEditing && selectedPlan && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>
                      {selectedPlan.title}
                      {selectedPlan.planType === 'Template' && <span style={typeBadge}>Modelo</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {selectedPlan.planType === 'Template' ? 'Modelo reutilizável' : (selectedPlan.userName || '—')} • {selectedPlan.workoutDays?.length || 0} dias
                      {selectedPlan.sourceTemplateId ? ` • copiado do modelo #${selectedPlan.sourceTemplateId}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selectedPlan.canManage && <button onClick={startEdit} style={primaryBtn}>✏️ Editar</button>}
                    {selectedPlan.canManage && selectedPlan.planType === 'Template' && (
                      <>
                        <button onClick={() => openCopyDialog('assign')} style={secondaryBtn}>👤 Atribuir a atleta</button>
                        <button onClick={() => openCopyDialog('copy-template')} style={secondaryBtn}>📑 Duplicar modelo</button>
                      </>
                    )}
                    {selectedPlan.canManage && selectedPlan.planType !== 'Template' && (
                      <>
                        <button onClick={() => openCopyDialog('copy-template')} style={secondaryBtn}>📑 Guardar como modelo</button>
                        <button onClick={() => openCopyDialog('copy-member')} style={secondaryBtn}>👥 Copiar para outro atleta</button>
                      </>
                    )}
                    {!selectedPlan.canManage && <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>Só leitura</span>}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <PlanCanvas days={selectedPlan.workoutDays || []} narrow={narrow} editing={false} readOnly />
                </div>
              </>
            )}
          </div>

          {/* Right: inspector + library quick panel */}
          <div style={card} ref={inspectorRef} data-testid="studio-inspector">
            <div style={{ fontWeight: 900, marginBottom: 10 }}>Inspector</div>

            {/* Recuperação do atleta + como ele vê o plano: o contexto que falta para decidir. */}
            <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border-color)' }} data-testid="studio-context">
              {contextMemberId ? (
                <>
                  <RecoveryHeatmap userId={contextMemberId} compact />
                  <div style={{ marginTop: 12 }}>
                    <PlanPreview days={isEditing ? (draft.workoutDays || []) : (selectedPlan?.workoutDays || [])} memberName={contextMemberName} />
                  </div>
                  <DeloadControl userId={contextMemberId} />
                </>
              ) : (
                <p className="ps-note" data-testid="recovery-no-member">
                  Escolha o atleta e guarde o plano para ver a recuperação muscular dele e como ele verá o treino no telemóvel.
                </p>
              )}
            </div>
            {!inspector && (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Selecione um exercício para editar sets/reps/descanso e media.
              </div>
            )}

            {inspector && (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ width: 70, height: 70, borderRadius: 14, background: 'var(--info-bg)', overflow: 'hidden' }}>
                    {(inspector.ex.gifUrl || inspector.ex.imageUrl) ? (
                      <img alt="exercise" src={(inspector.ex.gifUrl || inspector.ex.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : null}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 900 }}>{inspector.ex.name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{inspector.day.dayName}</div>
                  </div>
                </div>

                <label style={label}>Nome</label>
                <input value={inspector.ex.name} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'name', e.target.value)} style={input} />

                <div style={{ marginTop: 6, fontSize: 12, fontWeight: 900, color: 'var(--info)' }}>
                  {formatPrescription(inspector.ex) || 'Sem prescrição'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={label}>Séries</label>
                    <input type="number" min="1" value={inspector.ex.setsTarget} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'setsTarget', e.target.value)} style={input} />
                  </div>
                  <div>
                    <label style={label}>{inspector.ex.trackingType === 'time' ? 'Seg. mín.' : 'Reps mín.'}</label>
                    <input type="number" min="1" value={inspector.ex.repsMin} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'repsMin', e.target.value)} style={input} />
                  </div>
                  <div>
                    <label style={label}>{inspector.ex.trackingType === 'time' ? 'Seg. máx.' : 'Reps máx.'}</label>
                    <input type="number" min="1" value={inspector.ex.repsMax} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'repsMax', e.target.value)} style={input} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={label}>Descanso (s)</label>
                    <input type="number" min="0" value={inspector.ex.restSeconds} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'restSeconds', e.target.value)} style={input} />
                  </div>
                  <div>
                    <label style={label}>Carga (kg)</label>
                    <input type="number" min="0" step="0.5" value={inspector.ex.targetWeightKg} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'targetWeightKg', e.target.value)} style={input} />
                  </div>
                  <div>
                    <label style={label}>Cadência</label>
                    <input placeholder="3-1-2-0" value={inspector.ex.tempo} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'tempo', e.target.value)} style={input} />
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  {inspector.ex.id && editorMode === 'edit' ? (
                    <div style={{ marginTop: 12 }}>
                      <button onClick={() => openReplace(inspector.ex)} disabled={saving} style={secondaryBtn}>🔁 Substituir exercício</button>
                      {inspector.ex.isLegacy && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                          Sem ligação à biblioteca: ao substituir, passa a estar ligado.
                        </div>
                      )}
                      {inspector.ex.replacedAt && inspector.ex.originalName && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Original: {inspector.ex.originalName}</div>
                      )}

                      {/* Superset/circuito: 2 ou 3 exercícios do MESMO dia, feitos seguidos (A1, A2…). O descanso passa a
                          ser entre rondas e a prescrição de cada um não muda. */}
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-color)' }} data-testid="studio-superset">
                        <label style={label}>Superset / circuito</label>
                        {supersetMembersOf(inspector.day, inspector.ex).length >= 2 ? (
                          <>
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                              Grupo {String.fromCharCode(64 + inspector.ex.supersetGroup)}:{' '}
                              {supersetMembersOf(inspector.day, inspector.ex).map((x, i) => `${String.fromCharCode(64 + inspector.ex.supersetGroup)}${i + 1} ${x.name}`).join(' · ')}
                            </p>
                            <button onClick={removeSuperset} disabled={saving} style={secondaryBtn} data-testid="studio-superset-remove">
                              Desagrupar
                            </button>
                          </>
                        ) : (
                          <>
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                              Junte este a 1 ou 2 exercícios deste dia. No treino aparecem como A1/A2 e o descanso é entre rondas.
                            </p>
                            {(inspector.day.exercises || []).filter((x) => x.id && x.id !== inspector.ex.id).map((x) => {
                              const picked = supersetPick.forId === inspector.ex.id ? supersetPick.ids : [];
                              const checked = picked.includes(x.id);
                              return (
                                <label key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, fontSize: 13 }} data-testid={`studio-superset-pick-${x.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={saving || (!checked && picked.length >= 2)}
                                    onChange={() => setSupersetPick((cur) => {
                                      const base = cur.forId === inspector.ex.id ? cur.ids : [];
                                      return { forId: inspector.ex.id, ids: checked ? base.filter((v) => v !== x.id) : [...base, x.id] };
                                    })}
                                    style={{ width: 20, height: 20 }}
                                  />
                                  {x.name || `Exercício ${x.orderIndex + 1}`}
                                </label>
                              );
                            })}
                            <button onClick={saveSuperset} disabled={saving} style={secondaryBtn} data-testid="studio-superset-save">
                              Juntar em superset
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                <label style={label}>Notas</label>
                <textarea value={inspector.ex.notes} onChange={(e) => updateExerciseField(selectedExercisePath.dayIndex, selectedExercisePath.exIndex, 'notes', e.target.value)} style={{ ...input, minHeight: 80 }} />

                <p className="ps-note" style={{ marginTop: 10 }} data-testid="inspector-media-note">
                  {inspector.ex.exerciseLibraryId
                    ? 'A imagem e a demonstração vêm da biblioteca (ficheiros validados). Para as trocar, edite o exercício na biblioteca — não se escreve um endereço aqui.'
                    : 'Exercício escrito à mão: não tem ficha na biblioteca, por isso não tem imagem nem demonstração. Ligue-o à biblioteca para as ter.'}
                </p>
              </>
            )}

            <div style={{ height: 1, background: 'var(--bg-tertiary)', margin: '14px 0' }} />

            <div style={{ fontWeight: 900, marginBottom: 10 }}>{narrow ? 'Biblioteca' : 'Biblioteca (drag)'}</div>
            {isEditing && (draft.workoutDays || []).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label style={label} htmlFor="studio-add-target">Adicionar ao dia</label>
                <select id="studio-add-target" data-testid="add-target-day" value={Math.min(addTargetDay, (draft.workoutDays || []).length - 1)}
                  onChange={(e) => setAddTargetDay(Number(e.target.value))} style={input}>
                  {(draft.workoutDays || []).map((d, i) => <option key={i} value={i}>{d.dayName || `Dia ${i + 1}`}</option>)}
                </select>
              </div>
            )}
            <input value={libSearch} onChange={(e) => setLibSearch(e.target.value)} placeholder="Pesquisar…" style={input} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <select value={libFilters.muscleGroup} onChange={(e) => setLibFilters(prev => ({ ...prev, muscleGroup: e.target.value }))} style={input}>
                <option value="">Músculo</option>
                {muscleOptions.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select value={libFilters.equipment} onChange={(e) => setLibFilters(prev => ({ ...prev, equipment: e.target.value }))} style={input}>
                <option value="">Equip.</option>
                {equipmentOptions.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={loadLibrary} style={secondaryBtn} disabled={libLoading}>{libLoading ? '⏳' : '🔄 Atualizar'}</button>
              <button onClick={() => setShowNewLibModal(true)} style={primaryBtn}>➕ Exercício</button>
            </div>

            <div style={{ marginTop: 10, maxHeight: 360, overflow: 'auto' }}>
              {(library || []).slice(0, 60).map(x => (
                <div
                  key={x.id}
                  draggable
                  onDragStart={(e) => onDragStartLibrary(e, x)}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: 10,
                    borderRadius: 10,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-card)',
                    cursor: 'grab',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--info-bg)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(x.gifUrl || x.imageUrl) ? (
                      <img alt="exercise" src={(x.gifUrl || x.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 18 }}>🧩</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{x.muscleGroup || '—'} • {x.equipment || '—'}</div>
                  </div>
                  <button type="button" style={miniBtn} aria-label={`Ver como se faz: ${x.name}`} data-testid={`demo-lib-${x.id}`}
                    onClick={() => openLibraryDemo(x)} title="Ver como se faz">👁</button>
                  {isEditing && (draft.workoutDays || []).length > 0 && (
                    <button type="button" style={miniBtn} aria-label={`Adicionar ${x.name} ao dia escolhido`} data-testid={`add-lib-${x.id}`}
                      onClick={() => appendLibraryExercise(Math.min(addTargetDay, (draft.workoutDays || []).length - 1), x)}>➕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showGenerator && (
        <PlanGeneratorDialog members={userList} onClose={() => setShowGenerator(false)} onUse={startFromGenerated} />
      )}

      {copyDialog && selectedPlan && (
        <div style={modalBackdrop} onClick={() => !saving && setCopyDialog(null)}>
          <div style={{ ...card, width: 'min(480px, 100%)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 6 }}>
              {copyDialog.mode === 'assign' ? 'Atribuir modelo a um atleta'
                : copyDialog.mode === 'copy-member' ? 'Copiar plano para outro atleta' : 'Guardar como modelo'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              {copyDialog.mode === 'assign'
                ? 'O atleta recebe uma cópia independente: alterar o modelo depois não muda o plano dele, nem o contrário.'
                : 'Cria uma cópia independente de "' + selectedPlan.title + '".'}
            </div>

            {copyDialog.mode !== 'copy-template' && (
              <>
                <label style={label}>Atleta</label>
                <select value={copyDialog.userId} onChange={(e) => setCopyDialog(prev => ({ ...prev, userId: e.target.value }))} style={input}>
                  <option value="">—</option>
                  {userList.filter(u => u.id !== selectedPlan.userId).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </>
            )}

            <label style={{ ...label, marginTop: 10 }}>Título</label>
            <input value={copyDialog.title} onChange={(e) => setCopyDialog(prev => ({ ...prev, title: e.target.value }))} style={input} />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setCopyDialog(null)} disabled={saving} style={secondaryBtn}>Cancelar</button>
              <button onClick={submitCopyDialog} disabled={saving} style={primaryBtn}>{saving ? '⏳ A guardar…' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}

      {replaceDialog && (
        <div style={modalBackdrop} onClick={() => !saving && setReplaceDialog(null)}>
          <div style={{ ...card, width: 'min(560px, 100%)', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 4 }}>Substituir "{replaceDialog.name}"</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              A linha do plano é a mesma: mantém a posição e, quando o tipo de registo é igual, a prescrição. O exercício original fica guardado.
            </div>

            {(replaceDialog.loading || replaceDialog.alternatives.length > 0) && (
              <>
                <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Alternativas sugeridas</div>
                {replaceDialog.loading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>⏳ A carregar…</div>}
                {replaceDialog.alternatives.map(a => (
                  <div key={a.exerciseId} className="ps-ex" data-testid={`suggested-${a.exerciseId}`} style={{ cursor: 'default' }}>
                    <span className="ps-ex-thumb" aria-hidden="true">{a.imageUrl ? <img src={a.imageUrl} alt="" loading="lazy" /> : '🏋️'}</span>
                    <span className="ps-ex-main">
                      <span className="ps-ex-name">{a.name}</span>
                      <span className="ps-ex-rx">{a.kindLabel || a.kind}{a.note ? ` · ${a.note}` : ''}</span>
                      <span className="ps-ex-flags">
                        {a.source === 'auto'
                          ? <span className="ps-chip" style={{ cursor: 'default', padding: '2px 8px', fontSize: 11 }}>Proposto pelo motor</span>
                          : <span className="ps-chip" style={{ cursor: 'default', padding: '2px 8px', fontSize: 11 }}>Escolhido por uma pessoa</span>}
                        {!a.available ? <span className="ps-chip" style={{ cursor: 'default', padding: '2px 8px', fontSize: 11 }}>Equipamento indisponível neste ginásio</span> : null}
                        {(a.equipmentNames || []).slice(0, 2).map(n => <span key={n} className="ps-chip" style={{ cursor: 'default', padding: '2px 8px', fontSize: 11 }}>{n}</span>)}
                      </span>
                    </span>
                    <button onClick={() => replaceExercise({ id: a.exerciseId })} disabled={saving || !a.available} style={primaryBtn}
                      title={a.available ? 'Usar este exercício' : 'Este exercício precisa de equipamento que o ginásio não tem'}>Usar</button>
                  </div>
                ))}
                <div style={{ height: 1, background: 'var(--bg-tertiary)', margin: '12px 0' }} />
              </>
            )}
            {!replaceDialog.loading && replaceDialog.alternatives.length === 0 ? (
              <p className="ps-note" data-testid="no-alternatives">
                Sem sugestões para este exercício. Use a pesquisa abaixo para escolher outro da biblioteca.
              </p>
            ) : null}

            <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Qualquer exercício da biblioteca</div>
            <input value={replaceDialog.search} onChange={(e) => setReplaceDialog(prev => ({ ...prev, search: e.target.value }))} placeholder="Pesquisar…" style={input} />
            <div style={{ marginTop: 8 }}>
              {(library || [])
                .filter(x => x.isActive !== false && (!replaceDialog.search.trim() || String(x.name || '').toLowerCase().includes(replaceDialog.search.trim().toLowerCase())))
                .slice(0, 20)
                .map(x => (
                  <div key={x.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 8, border: '1px solid var(--border-color)', borderRadius: 10, marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 900 }}>{x.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{x.muscleGroup || '—'} • {x.equipment || '—'}</div>
                    </div>
                    <button onClick={() => replaceExercise(x)} disabled={saving} style={secondaryBtn}>Usar</button>
                  </div>
                ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setReplaceDialog(null)} disabled={saving} style={secondaryBtn}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'library' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16 }}>🧩 Biblioteca de Exercícios</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Imagens/GIFs ficam em /uploads (usa upload automático).</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={loadLibrary} style={secondaryBtn} disabled={libLoading}>{libLoading ? '⏳' : '🔄 Atualizar'}</button>
              <button onClick={() => setShowNewLibModal(true)} style={primaryBtn}>➕ Novo exercício</button>
            </div>
          </div>

          <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 220px 220px 220px', gap: 12, marginTop: 12 }}>
            <input value={libSearch} onChange={(e) => setLibSearch(e.target.value)} placeholder="Pesquisar…" style={input} />
            <select value={libFilters.muscleGroup} onChange={(e) => setLibFilters(prev => ({ ...prev, muscleGroup: e.target.value }))} style={input}>
              <option value="">Músculo</option>
              {muscleOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={libFilters.equipment} onChange={(e) => setLibFilters(prev => ({ ...prev, equipment: e.target.value }))} style={input}>
              <option value="">Equipamento</option>
              {equipmentOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input value={libFilters.difficulty} onChange={(e) => setLibFilters(prev => ({ ...prev, difficulty: e.target.value }))} placeholder="Dificuldade" style={input} />
          </div>

          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 12 }}>
            {(library || []).map(x => (
              <div key={x.id} style={{ border: '1px solid var(--border-color)', borderRadius: 12, background: 'var(--bg-card)', overflow: 'hidden' }}>
                <div style={{ height: 160, background: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(x.gifUrl || x.imageUrl) ? (
                    <img alt="exercise" src={(x.gifUrl || x.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ color: '#fff', fontWeight: 900, opacity: 0.8 }}>Sem imagem</div>
                  )}
                </div>
                <div style={{ padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <div style={{ fontWeight: 900 }}>{x.name}</div>
                    <span style={{ fontSize: 12, fontWeight: 900, color: x.isActive ? '#16a34a' : '#ef4444' }}>{x.isActive ? 'ATIVO' : 'INATIVO'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{x.muscleGroup || '—'} • {x.equipment || '—'} • {x.difficulty || '—'}</div>
                  {x.instructions && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--info-text)' }}>{x.instructions}</div>}
                  {x.videoUrl && <a href={x.videoUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontWeight: 900, color: 'var(--info)', textDecoration: 'none' }}>▶ Ver vídeo</a>}
                </div>
              </div>
            ))}
            {library.length === 0 && !libLoading && (
              <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)' }}>Sem exercícios na biblioteca.</div>
            )}
          </div>
        </div>
      )}

      {showNewLibModal && (
        <div style={modalOverlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>➕ Novo exercício (biblioteca)</div>
              <button onClick={() => setShowNewLibModal(false)} style={miniBtnDanger}>✖</button>
            </div>

            <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={label}>Nome</label>
                <input value={libForm.name} onChange={(e) => setLibForm(prev => ({ ...prev, name: e.target.value }))} style={input} />
              </div>
              <div>
                <label style={label}>Dificuldade</label>
                <select value={libForm.difficulty} onChange={(e) => setLibForm(prev => ({ ...prev, difficulty: e.target.value }))} style={input}>
                  {['Iniciante', 'Intermédio', 'Avançado'].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Músculo</label>
                <select value={libForm.muscleGroup} onChange={(e) => setLibForm(prev => ({ ...prev, muscleGroup: e.target.value }))} style={input}>
                  <option value="">Selecionar…</option>
                  {muscleOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {(selectedMuscleMeta?.imageUrl || '').trim() && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <img alt="muscle" src={selectedMuscleMeta.imageUrl} style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border-color)' }} />
                    <div style={{ fontSize: 12, color: 'var(--info)' }}>Imagem do tipo de músculo</div>
                  </div>
                )}
              </div>
              <div>
                <label style={label}>Equipamento</label>
                <select value={libForm.equipment} onChange={(e) => setLibForm(prev => ({ ...prev, equipment: e.target.value }))} style={input}>
                  <option value="">Selecionar…</option>
                  {equipmentOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {(selectedEquipmentMeta?.imageUrl || '').trim() && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <img alt="equipment" src={selectedEquipmentMeta.imageUrl} style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border-color)' }} />
                    <div style={{ fontSize: 12, color: 'var(--info)' }}>Imagem do tipo de equipamento</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={label}>Instruções (curtas)</label>
              <textarea value={libForm.instructions} onChange={(e) => setLibForm(prev => ({ ...prev, instructions: e.target.value }))} style={{ ...input, minHeight: 80 }} />
            </div>

            <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={label}>Vídeo de demonstração</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={libVideoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" style={{ display: 'none' }} onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const url = await uploadToFolder(f, 'exercise-library');
                      setLibForm(prev => ({ ...prev, videoUrl: url || prev.videoUrl }));
                      toast.success('Vídeo enviado ✅');
                    } catch {
                      toast.error('Não foi possível enviar o vídeo.');
                    } finally {
                      e.target.value = '';
                    }
                  }} />
                  <button onClick={() => libVideoInputRef.current?.click()} style={secondaryBtn} data-testid="upload-video">📤 Enviar vídeo</button>
                  {(libForm.videoUrl || '').trim() ? (
                    <button onClick={() => setLibForm(prev => ({ ...prev, videoUrl: '' }))} style={miniBtnDanger} title="Remover o vídeo">🗑️</button>
                  ) : null}
                </div>
                {(libForm.videoUrl || '').trim() ? (
                  <div style={{ marginTop: 8, border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }} data-testid="video-preview">
                    <video src={libForm.videoUrl} controls playsInline style={{ width: '100%', height: 160, display: 'block', background: '#000' }} />
                  </div>
                ) : (
                  <div className="ps-note" style={{ marginTop: 6 }}>Sem vídeo. A demonstração só chega aos atletas depois de validada por uma pessoa.</div>
                )}
              </div>
              <div>
                <label style={label}>Calorias/min (opcional)</label>
                <input type="number" value={libForm.caloriesPerMinute} onChange={(e) => setLibForm(prev => ({ ...prev, caloriesPerMinute: e.target.value }))} style={input} />
              </div>
            </div>

            <div className={narrow ? 'wps-stack' : undefined} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={label}>Imagem</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={libFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const url = await uploadToFolder(f, 'exercise-library');
                      setLibForm(prev => ({ ...prev, imageUrl: url || prev.imageUrl }));
                      toast.success('Imagem enviada ✅');
                    } catch {
                      toast.error('Erro no upload');
                    } finally {
                      e.target.value = '';
                    }
                  }} />
                  <button onClick={() => libFileInputRef.current?.click()} style={secondaryBtn} data-testid="upload-image">📤 Enviar imagem</button>
                  {(libForm.imageUrl || '').trim() ? (
                    <button onClick={() => setLibForm(prev => ({ ...prev, imageUrl: '' }))} style={miniBtnDanger} title="Remover a imagem">🗑️</button>
                  ) : null}
                </div>
                {(libForm.imageUrl || '').trim() ? (
                  <div style={{ marginTop: 8, border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden', background: 'var(--info-bg)' }} data-testid="image-preview">
                    <img
                      alt="Pré-visualização da imagem do exercício"
                      src={libForm.imageUrl}
                      style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                ) : null}
              </div>
              <div>
                <label style={label}>GIF / animação</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={libGifInputRef} type="file" accept="image/gif,image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const url = await uploadToFolder(f, 'exercise-library');
                      setLibForm(prev => ({ ...prev, gifUrl: url || prev.gifUrl }));
                      toast.success('GIF enviado ✅');
                    } catch {
                      toast.error('Erro no upload');
                    } finally {
                      e.target.value = '';
                    }
                  }} />
                  <button onClick={() => libGifInputRef.current?.click()} style={secondaryBtn} data-testid="upload-gif">📤 Enviar GIF</button>
                  {(libForm.gifUrl || '').trim() ? (
                    <button onClick={() => setLibForm(prev => ({ ...prev, gifUrl: '' }))} style={miniBtnDanger} title="Remover o GIF">🗑️</button>
                  ) : null}
                </div>
                {(libForm.gifUrl || '').trim() ? (
                  <div style={{ marginTop: 8, border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden', background: 'var(--info-bg)' }} data-testid="gif-preview">
                    <img
                      alt="Pré-visualização do GIF do exercício"
                      src={libForm.gifUrl}
                      style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <p className="ps-note" style={{ marginTop: 8 }} data-testid="media-policy">
              Os ficheiros entram por upload e ficam <strong>à espera de validação</strong> na biblioteca (quem valida fica registado).
              Não há campos de endereço: uma imagem ou um vídeo copiados de outro sítio sem licença e atribuição não entram no produto.
            </p>

            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 800, color: 'var(--info-text)' }}>
                <input type="checkbox" checked={!!libForm.isActive} onChange={(e) => setLibForm(prev => ({ ...prev, isActive: e.target.checked }))} />
                Ativo
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setShowNewLibModal(false)} style={dangerBtn}>Cancelar</button>
              <button onClick={saveLibraryItem} style={primaryBtn}>Guardar</button>
            </div>
          </div>
        </div>
      )}


      {/* Ver como se faz: o mesmo visualizador do treino do atleta, para escolher o exercício com os olhos. */}
      <DemoOverlay open={!!libraryDemo} demo={libraryDemo?.demo} onClose={() => setLibraryDemo(null)} stacked />
    </div>
  );
};

const card = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: 14,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  padding: 14,
};

const typeBadge = {
  marginLeft: 8,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
  background: 'var(--info-bg)',
  color: 'var(--info)',
  border: '1px solid var(--info)',
};

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
};

const label = {
  display: 'block',
  fontSize: 12,
  fontWeight: 900,
  color: 'var(--info-text)',
  marginBottom: 6,
};

const input = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid var(--border-color)',
  outline: 'none',
  fontSize: 13,
  boxSizing: 'border-box',
};

const tabBtn = (active) => ({
  background: active ? '#0f172a' : '#fff',
  color: active ? '#fff' : '#0f172a',
  border: '1px solid var(--border-color)',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
});

const primaryBtn = {
  background: 'var(--info)',
  color: '#fff',
  border: 'none',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const secondaryBtn = {
  background: 'var(--bg-card)',
  color: 'var(--info-text)',
  border: '1px solid var(--border-color)',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const dangerBtn = {
  background: 'var(--bg-card)',
  color: 'var(--error)',
  border: '1px solid var(--error)',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const miniBtn = {
  background: 'var(--bg-card)',
  color: 'var(--info-text)',
  border: '1px solid var(--border-color)',
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
  fontSize: 12,
};

const miniBtnDanger = {
  background: 'var(--bg-card)',
  color: 'var(--error)',
  border: '1px solid var(--error)',
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
  fontSize: 12,
};

const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 16,
};

const modal = {
  width: 'min(980px, 100%)',
  maxHeight: '92vh',
  overflow: 'auto',
  boxSizing: 'border-box',
  background: 'var(--bg-card)',
  borderRadius: 14,
  border: '1px solid var(--border-color)',
  boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
  padding: 14,
};

export default AdminWorkoutPlanStudioPage;
